import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const TALENT_ROLE_OPTIONS = [
  { value: "", label: "All roles" },
  { value: "train-driver", label: "Train driver" },
  { value: "conductor", label: "Conductor" },
  { value: "depot-driver", label: "Depot driver" },
  { value: "maintenance-rolling-stock", label: "Maintenance (Rolling Stock)" },
  { value: "maintenance-track-infrastructure", label: "Maintenance (Track or Infrastructure)" },
  { value: "administrative", label: "Administrative" },
  { value: "graduate", label: "Graduate" },
  { value: "signalling", label: "Signalling" },
  { value: "other", label: "Other" },
];

const CHECK_LABELS = {
  right_to_work_uk: "Right to work in the UK",
  minimum_working_age: "Meets minimum working age",
  english_communication: "Communicates effectively in English",
  basic_literacy_numeracy_digital: "Basic literacy, numeracy and digital skills",
  prepared_for_identity_reference_checks: "Prepared for identity and reference checks",
  medical_disclosure_honesty: "Committed to honest medical disclosure",
  understands_shift_patterns: "Understands railway shift patterns",
  can_travel_without_public_transport: "Can travel without public transport",
  willing_to_complete_training: "Willing to complete required training",
  motivation_for_railway_career: "Motivated for a railway career",
};

const ROLE_LABELS = Object.fromEntries(TALENT_ROLE_OPTIONS.map((r) => [r.value, r.label]));
const prettyRole = (value) => ROLE_LABELS[value] || value || "-";
const outcomeLabel = (value) => {
  const key = String(value || "").toLowerCase();
  if (key === "pass") return "Progression standard met";
  if (key === "review_required") return "Review required";
  if (key === "not_met") return "Progression standard not met";
  return "Not completed";
};

const PROFILE_LABELS = {
  R: "Reflective risk-checking",
  T: "Traditional procedural discipline",
  P: "Pragmatic delivery focus",
  A: "Assertive direct intervention",
};

function explainCombinedProfile(code) {
  const norm = String(code || "").toUpperCase();
  const first = norm[0];
  const second = norm[1];
  if (!first || !second) return "Combined profile not yet available.";
  const firstLabel = PROFILE_LABELS[first] || first;
  const secondLabel = PROFILE_LABELS[second] || second;
  return `${first}${second} means the strongest tendency is ${firstLabel}, with ${secondLabel} as the secondary style under pressure.`;
}

function outcomeHint(stageTwo) {
  if (!stageTwo) return null;
  const outcome = String(stageTwo.status || "").toLowerCase();
  if (outcome !== "not_met") return null;
  const critical = Number(stageTwo?.scores?.critical_response_count || 0);
  const redLine = Number(stageTwo?.scores?.absolute_red_line_count || 0);
  const flags = stageTwo?.decision?.assessment_integrity_flags || [];
  if (critical > 0 || redLine > 0 || flags.length > 0) {
    return "Outcome is rule-based: high overall score can still be not met when critical/red-line responses or integrity flags are present.";
  }
  return "Outcome is based on threshold rules across safety score and domain criteria, not only the overall percentage.";
}

export default function TalentPoolCandidatesExplorer() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const isAdmin = user?.role === "admin";
  const accessAllowed = isAdmin || (user?.role === "employer" && user?.approved === true && user?.email_verified === true);

  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState([]);

  const [q, setQ] = useState("");
  const [roleInterest, setRoleInterest] = useState("");
  const [roleSuitable, setRoleSuitable] = useState("");
  const [postcode, setPostcode] = useState("");
  const [recruiterPostcode, setRecruiterPostcode] = useState("");
  const [maxDistanceKm, setMaxDistanceKm] = useState("80");

  const [selectedIds, setSelectedIds] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [profilesById, setProfilesById] = useState({});

  const loadCandidates = async () => {
    if (!accessAllowed) return;
    setLoading(true);
    try {
      const response = await api.get("/talent-pool/candidates", {
        params: {
          q: q || undefined,
          role_interest: roleInterest || undefined,
          role_suitable: roleSuitable || undefined,
          postcode: postcode || undefined,
          recruiter_postcode: recruiterPostcode || undefined,
          max_distance_km: recruiterPostcode ? (parseInt(maxDistanceKm, 10) || 80) : undefined,
          limit: 140,
        },
      });
      setCandidates(response.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load candidates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCandidates();
  }, [accessAllowed]);

  useEffect(() => {
    const validIds = new Set(candidates.map((c) => c.id));
    const kept = selectedIds.filter((id) => validIds.has(id));
    if (kept.length !== selectedIds.length) {
      setSelectedIds(kept);
    }
    if (activeId && !validIds.has(activeId)) {
      setActiveId(kept[0] || null);
    }
  }, [candidates]);

  useEffect(() => {
    const loadProfiles = async () => {
      if (selectedIds.length === 0) {
        setProfilesById({});
        setActiveId(null);
        return;
      }
      setProfilesLoading(true);
      try {
        const response = await api.get("/talent-pool/candidates/one-pager", {
          params: {
            candidate_ids: selectedIds.join(","),
          },
        });
        const map = {};
        (response.data || []).forEach((row) => {
          const cid = row?.candidate?.id;
          if (cid) map[cid] = row;
        });
        setProfilesById(map);
        if (!activeId || !map[activeId]) {
          setActiveId(selectedIds.find((id) => map[id]) || null);
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || "Could not load candidate one-pagers");
      } finally {
        setProfilesLoading(false);
      }
    };

    loadProfiles();
  }, [selectedIds.join(",")]);

  const activeProfile = activeId ? profilesById[activeId] : null;

  const selectedProfiles = useMemo(
    () => selectedIds.map((id) => profilesById[id]).filter(Boolean),
    [selectedIds, profilesById]
  );

  const toggleSelected = (candidateId) => {
    setSelectedIds((prev) => {
      if (prev.includes(candidateId)) {
        const next = prev.filter((id) => id !== candidateId);
        if (activeId === candidateId) {
          setActiveId(next[0] || null);
        }
        return next;
      }
      if (prev.length >= 4) {
        toast.error("Select up to 4 candidates to compare.");
        return prev;
      }
      return [...prev, candidateId];
    });
  };

  const openOnePager = (candidateId) => {
    if (!selectedIds.includes(candidateId)) {
      toggleSelected(candidateId);
    }
    setActiveId(candidateId);
  };

  const clearSelection = () => {
    setSelectedIds([]);
    setActiveId(null);
    setProfilesById({});
  };

  const handleLogout = () => {
    logout();
    nav("/");
  };

  if (!accessAllowed) {
    return (
      <div className="min-h-screen bg-background">
        <div className="mx-auto max-w-5xl px-4 py-10">
          <div className="rounded-lg border border-amber/40 bg-amber/10 p-6">
            <h1 className="font-display text-2xl font-bold text-ink">Candidate explorer is locked</h1>
            <p className="mt-2 text-sm text-ink/80">
              Only admin users and verified, approved employers can access this page.
            </p>
            <div className="mt-4">
              <Button asChild variant="outline">
                <Link to={isAdmin ? "/admin/dashboard" : "/employer-portal/dashboard"}>Back to dashboard</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Talent Pool</div>
              <h1 className="mt-1 font-display text-2xl font-bold text-ink">Candidate Explorer and Comparison</h1>
              <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
                Select candidates to open a one-page profile with Stage 1 responses and Stage 2 profile signals. Compare candidates side by side and filter by role interest, suitability and postcode.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button asChild variant="outline">
                <Link to={isAdmin ? "/admin/dashboard" : "/employer-portal/dashboard"}>Back to dashboard</Link>
              </Button>
              <Button variant="outline" onClick={handleLogout}>Log out</Button>
            </div>
          </div>

          <div className="mt-5 rounded-lg border border-border bg-background p-4">
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-6">
              <div>
                <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Search</Label>
                <Input className="mt-1" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, role, location" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Interested role</Label>
                <select className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2" value={roleInterest} onChange={(e) => setRoleInterest(e.target.value)}>
                  {TALENT_ROLE_OPTIONS.map((opt) => (
                    <option key={`interest-${opt.value || "all"}`} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Suitable role</Label>
                <select className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2" value={roleSuitable} onChange={(e) => setRoleSuitable(e.target.value)}>
                  {TALENT_ROLE_OPTIONS.map((opt) => (
                    <option key={`suitable-${opt.value || "all"}`} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Candidate postcode</Label>
                <Input className="mt-1 uppercase" value={postcode} onChange={(e) => setPostcode(e.target.value)} placeholder="M1" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Recruiter postcode</Label>
                <Input className="mt-1 uppercase" value={recruiterPostcode} onChange={(e) => setRecruiterPostcode(e.target.value)} placeholder="M1 2AB" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Max distance (km)</Label>
                <Input className="mt-1" type="number" min={5} max={400} value={maxDistanceKm} onChange={(e) => setMaxDistanceKm(e.target.value)} />
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button className="bg-navy text-white hover:bg-lime-600 hover:text-slate-950" onClick={loadCandidates}>Refresh list</Button>
              <Button variant="outline" onClick={clearSelection}>Clear selection</Button>
              <div className="inline-flex items-center rounded-full border border-sky/30 bg-sky/10 px-3 py-1 text-xs font-semibold text-ink">
                {selectedIds.length} selected
              </div>
            </div>
          </div>

          <div className="mt-5 rounded-lg border border-border overflow-hidden">
            <div className="max-h-[420px] overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-sand sticky top-0 z-10">
                  <tr className="text-left">
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Select</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Candidate</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Interested roles</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Suitable roles</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Postcode</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Distance</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan="7" className="px-4 py-8 text-center text-muted-foreground">Loading candidates...</td>
                    </tr>
                  )}
                  {!loading && candidates.length === 0 && (
                    <tr>
                      <td colSpan="7" className="px-4 py-8 text-center text-muted-foreground">No candidates match the selected filters.</td>
                    </tr>
                  )}
                  {!loading && candidates.map((candidate) => (
                    <tr key={candidate.id} className="border-t border-border hover:bg-paper/30">
                      <td className="px-4 py-3 align-top">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(candidate.id)}
                          onChange={() => toggleSelected(candidate.id)}
                          className="h-4 w-4"
                        />
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="font-semibold text-ink">{candidate.contact_name || "Candidate"}</div>
                        <div className="text-xs text-muted-foreground mt-1">{candidate.candidate_reference}</div>
                      </td>
                      <td className="px-4 py-3 align-top text-muted-foreground">
                        <div className="flex flex-wrap gap-1.5">
                          {(candidate.target_roles || []).length === 0 ? "-" : (candidate.target_roles || []).map((role) => (
                            <span key={`${candidate.id}-interest-${role}`} className="inline-flex items-center rounded-full border border-border bg-paper px-2 py-0.5 text-[11px] text-ink">
                              {prettyRole(role)}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top text-muted-foreground">
                        <div className="flex flex-wrap gap-1.5">
                          {(candidate.role_suitability || []).length === 0 ? "-" : (candidate.role_suitability || []).map((role) => (
                            <span key={`${candidate.id}-suitable-${role}`} className="inline-flex items-center rounded-full border border-lime-300 bg-lime-50 px-2 py-0.5 text-[11px] text-lime-900">
                              {prettyRole(role)}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top text-muted-foreground">
                        <div className="font-mono text-xs">{candidate.preferred_postcode || "-"}</div>
                      </td>
                      <td className="px-4 py-3 align-top text-muted-foreground">
                        {Number.isFinite(candidate.distance_km) ? `${candidate.distance_km} km` : "-"}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <Button size="sm" variant="outline" onClick={() => openOnePager(candidate.id)}>Open one-pager</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-5 grid gap-5 xl:grid-cols-2">
            <div className="rounded-lg border border-border bg-background p-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h2 className="font-display text-lg font-bold text-ink">Candidate One-Pager</h2>
                {profilesLoading && <div className="text-xs text-muted-foreground">Loading profile...</div>}
              </div>

              {!activeProfile && !profilesLoading && (
                <div className="mt-4 rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">
                  Select at least one candidate and open the one-pager.
                </div>
              )}

              {activeProfile && (
                <div className="mt-4 space-y-4">
                  <div className="rounded-md border border-border p-3">
                    <div className="font-semibold text-ink">{activeProfile.candidate?.contact_name || "Candidate"}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{activeProfile.candidate?.candidate_reference}</div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {(activeProfile.candidate?.target_roles || []).map((role) => (
                        <span key={`detail-role-${role}`} className="inline-flex items-center rounded-full border border-border bg-paper px-2 py-0.5 text-[11px] text-ink">
                          {prettyRole(role)}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xs uppercase tracking-[0.14em] text-muted-foreground font-semibold">Stage 1 responses</h3>
                    <div className="mt-2 rounded-md border border-border overflow-hidden divide-y divide-border">
                      {Object.entries(CHECK_LABELS).map(([key, label]) => {
                        const pass = !!activeProfile.stage_one?.responses?.[key];
                        return (
                          <div key={`check-${key}`} className="flex items-center justify-between px-3 py-2 text-sm">
                            <span className="text-ink">{label}</span>
                            <span className={pass ? "font-semibold text-lime-700" : "font-semibold text-amber-700"}>{pass ? "Yes" : "No"}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xs uppercase tracking-[0.14em] text-muted-foreground font-semibold">Stage 2 profile</h3>
                    <div className="mt-2 rounded-xl border border-border bg-gradient-to-br from-sky-50 via-white to-lime-50 p-3 text-sm">
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <div className="rounded-lg border border-sky-200 bg-white/80 p-3">
                          <div className="text-xs text-muted-foreground uppercase tracking-wider">Outcome</div>
                          <div className="text-ink font-semibold mt-0.5">{outcomeLabel(activeProfile.stage_two?.status)}</div>
                        </div>
                        <div className="rounded-lg border border-sky-200 bg-white/80 p-3">
                          <div className="text-xs text-muted-foreground uppercase tracking-wider">Overall safety score</div>
                          <div className="text-ink font-semibold mt-0.5">{activeProfile.stage_two?.scores?.overall_safety_pct ?? "-"}%</div>
                        </div>
                        <div className="rounded-lg border border-sky-200 bg-white/80 p-3">
                          <div className="text-xs text-muted-foreground uppercase tracking-wider">Combined profile</div>
                          <div className="text-ink font-semibold mt-0.5">{activeProfile.stage_two?.profile?.combined_profile || "-"}</div>
                        </div>
                        <div className="rounded-lg border border-sky-200 bg-white/80 p-3">
                          <div className="text-xs text-muted-foreground uppercase tracking-wider">Strength</div>
                          <div className="text-ink font-semibold mt-0.5 capitalize">{activeProfile.stage_two?.profile?.profile_strength_indicator || "-"}</div>
                        </div>
                      </div>
                      <div className="mt-3 rounded-lg border border-sky-200 bg-white/90 p-3 text-xs text-ink leading-relaxed">
                        <div className="font-semibold text-ink">What the combined profile means</div>
                        <div className="mt-1 text-muted-foreground">{explainCombinedProfile(activeProfile.stage_two?.profile?.combined_profile)}</div>
                        <div className="mt-2 grid gap-1 text-[11px] sm:grid-cols-2">
                          <div><span className="font-semibold text-ink">R</span>: Reflective risk-checking</div>
                          <div><span className="font-semibold text-ink">T</span>: Traditional procedural discipline</div>
                          <div><span className="font-semibold text-ink">P</span>: Pragmatic delivery focus</div>
                          <div><span className="font-semibold text-ink">A</span>: Assertive direct intervention</div>
                        </div>
                      </div>
                      {outcomeHint(activeProfile.stage_two) && (
                        <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                          {outcomeHint(activeProfile.stage_two)}
                        </div>
                      )}
                      <div className="mt-3">
                        <div className="text-xs text-muted-foreground uppercase tracking-wider">Role suitability</div>
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          {(activeProfile.stage_two?.role_suitability || []).length === 0 ? (
                            <span className="text-muted-foreground">-</span>
                          ) : (activeProfile.stage_two?.role_suitability || []).map((role) => (
                            <span key={`fit-${role}`} className="inline-flex items-center rounded-full border border-lime-300 bg-lime-50 px-2 py-0.5 text-[11px] text-lime-900">
                              {prettyRole(role)}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xs uppercase tracking-[0.14em] text-muted-foreground font-semibold">Stage progression and placeholders</h3>
                    <div className="mt-2 grid gap-2 sm:grid-cols-2">
                      {(activeProfile.stages || []).map((stage) => (
                        <div key={stage.slug} className="rounded-md border border-border bg-card p-2.5">
                          <div className="text-xs uppercase tracking-wider text-muted-foreground">{stage.slug.replaceAll("_", " ")}</div>
                          <div className="text-sm font-semibold text-ink mt-0.5">{stage.label}</div>
                          <div className="text-xs mt-1 text-muted-foreground">{stage.note}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-lg border border-border bg-background p-4">
              <h2 className="font-display text-lg font-bold text-ink">Compare Selected Candidates</h2>
              {selectedProfiles.length < 2 ? (
                <div className="mt-4 rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">
                  Select at least 2 candidates to compare.
                </div>
              ) : (
                <div className="mt-4 space-y-4">
                  <div className="grid gap-2 sm:grid-cols-2">
                    {selectedProfiles.map((profile) => (
                      <button
                        key={`compare-head-${profile.candidate?.id}`}
                        type="button"
                        className={`rounded-md border p-3 text-left ${activeId === profile.candidate?.id ? "border-amber bg-amber/10" : "border-border bg-card"}`}
                        onClick={() => setActiveId(profile.candidate?.id)}
                      >
                        <div className="font-semibold text-ink">{profile.candidate?.contact_name || "Candidate"}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">{profile.candidate?.candidate_reference}</div>
                      </button>
                    ))}
                  </div>

                  <div className="rounded-md border border-border overflow-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-sand">
                        <tr className="text-left">
                          <th className="px-3 py-2 text-xs uppercase tracking-wider text-ink font-semibold">Metric</th>
                          {selectedProfiles.map((profile) => (
                            <th key={`metric-${profile.candidate?.id}`} className="px-3 py-2 text-xs uppercase tracking-wider text-ink font-semibold">{profile.candidate?.contact_name || "Candidate"}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-t border-border">
                          <td className="px-3 py-2 text-muted-foreground">Interested roles</td>
                          {selectedProfiles.map((profile) => (
                            <td key={`interest-${profile.candidate?.id}`} className="px-3 py-2 text-ink">{(profile.candidate?.target_roles || []).map(prettyRole).join(", ") || "-"}</td>
                          ))}
                        </tr>
                        <tr className="border-t border-border">
                          <td className="px-3 py-2 text-muted-foreground">Stage 1 overall</td>
                          {selectedProfiles.map((profile) => (
                            <td key={`s1-${profile.candidate?.id}`} className="px-3 py-2 text-ink">{profile.comparison_metrics?.overall_stage_one_pct ?? "-"}%</td>
                          ))}
                        </tr>
                        <tr className="border-t border-border">
                          <td className="px-3 py-2 text-muted-foreground">Stage 2 outcome</td>
                          {selectedProfiles.map((profile) => (
                            <td key={`s2o-${profile.candidate?.id}`} className="px-3 py-2 text-ink">{outcomeLabel(profile.comparison_metrics?.stage_two_progression_outcome)}</td>
                          ))}
                        </tr>
                        <tr className="border-t border-border">
                          <td className="px-3 py-2 text-muted-foreground">Stage 2 safety score</td>
                          {selectedProfiles.map((profile) => (
                            <td key={`s2s-${profile.candidate?.id}`} className="px-3 py-2 text-ink">{profile.comparison_metrics?.stage_two_overall_safety_pct ?? "-"}%</td>
                          ))}
                        </tr>
                        <tr className="border-t border-border">
                          <td className="px-3 py-2 text-muted-foreground">Combined profile</td>
                          {selectedProfiles.map((profile) => (
                            <td key={`cp-${profile.candidate?.id}`} className="px-3 py-2 text-ink">{profile.comparison_metrics?.combined_profile || "-"}</td>
                          ))}
                        </tr>
                        <tr className="border-t border-border">
                          <td className="px-3 py-2 text-muted-foreground">Role suitability</td>
                          {selectedProfiles.map((profile) => (
                            <td key={`fit-${profile.candidate?.id}`} className="px-3 py-2 text-ink">{(profile.stage_two?.role_suitability || []).map(prettyRole).join(", ") || "-"}</td>
                          ))}
                        </tr>
                        <tr className="border-t border-border">
                          <td className="px-3 py-2 text-muted-foreground">Postcode area</td>
                          {selectedProfiles.map((profile) => (
                            <td key={`post-${profile.candidate?.id}`} className="px-3 py-2 text-ink font-mono">{profile.candidate?.preferred_postcode || "-"}</td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
